<template>
  <div :class="{ 'code-input-container': true, [className]: !!className }">
    <p class="title" v-if="title">{{ title }}</p>
    <div class="code-input">
      <template>
        <input v-for="(v, index) in values" :type="type === 'number' ? 'tel' : type" @paste="handlePaste"
          :pattern="type === 'number' ? '[0-9]' : null" :autoFocus="autoFocus && index === autoFocusIndex" :style="{
            width: fieldWidth ? `${fieldWidth}px` : false,
            height: fieldHeight ? `${fieldHeight}px` : false,
          }" :key="`${id}-${index}`" :data-id="index" :value="v" :ref="iRefs[index]" @input="onValueChange"
          @focus="onFocus" @keydown="onKeyDown" :disabled="disabled" :required="required" maxlength="1"
          :aria-label="`Enter digit ${index + 1}`" />
      </template>
    </div>
  </div>
</template>

<script>
const KEY_CODE = { backspace: 8, left: 37, up: 38, right: 39, down: 40 };
export default {
  name: 'CodeInput',
  emits: ['update:modelValue'],
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    type: {
      type: String,
      default: 'number',
    },
    className: String,
    fields: {
      type: Number,
      default: 5,
    },
    fieldWidth: {
      type: Number,
      default: null,
    },
    fieldHeight: {
      type: Number,
      default: null,
    },
    autoFocus: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    title: String,
    change: Function,
    complete: Function,
  },
  data() {
    const { fields } = this;
    const vals = Array(fields).fill('');
    const autoFocusIndex = this.values && this.values.length ? Math.min(this.values.length, fields) : 0;

    this.iRefs = Array.from({ length: fields }, (_, i) => `input_${i}`);
    this.id = +new Date();

    return {
      values: vals,
      autoFocusIndex,
    };
  },
  methods: {
    handlePaste(e) {
      const pastedData = e.clipboardData.getData('text');
      if (!pastedData || !this.type === 'number') return;

      // Filter out non-digit characters if the input type is 'number'
      const filteredData = this.type === 'number' ? pastedData.replace(/[^\d]/g, '') : pastedData;

      // Distribute characters across input fields
      filteredData.split('').forEach((char, index) => {
        if (index < this.fields) {
          this.$set(this.values, index, char);
        }
      });

      // Prevent the default paste action
      e.preventDefault();

      // Trigger change event after pasting
      this.triggerChange();
    },
    onFocus(e) {
      e.target.select(e);
    },
    onValueChange(e) {
      const index = parseInt(e.target.dataset.id);
      const { type, fields } = this;
      let value = e.target.value;

      // Ensure numeric input for type 'number'
      if (type === 'number') {
        value = value.replace(/[^\d]/gi, '');
      }

      // Handle pasted values correctly
      if (value.length > 1 && index + value.length <= fields) {
        value.split('').forEach((char, i) => {
          const inputIndex = index + i;
          if (inputIndex < fields) {
            this.$set(this.values, inputIndex, char);
          }
        });

        // Focus on the next input field after pasting
        const nextInput = this.iRefs[Math.min(index + value.length, fields - 1)];
        if (nextInput) {
          this.$refs[nextInput][0].focus();
        }
      } else {
        this.$set(this.values, index, value);
        if (value && index < fields - 1) {
          this.$refs[this.iRefs[index + 1]][0].focus();
        }
      }

      this.triggerChange();
    },
    onKeyDown(e) {
      const index = parseInt(e.target.dataset.id);
      const prevIndex = index - 1;
      const nextIndex = index + 1;
      const prev = this.iRefs[prevIndex];
      const next = this.iRefs[nextIndex];
      switch (e.keyCode) {
        case KEY_CODE.backspace: {
          e.preventDefault();
          const vals = [...this.values];
          if (this.values[index]) {
            vals[index] = '';
            this.values = vals;
            this.triggerChange(vals);
          } else if (prev) {
            vals[prevIndex] = '';
            this.$refs[prev][0].focus();
            this.values = vals;
            this.triggerChange(vals);
          }
          break;
        }
        case KEY_CODE.left:
          e.preventDefault();
          if (prev) {
            this.$refs[prev][0].focus();
          }
          break;
        case KEY_CODE.right:
          e.preventDefault();
          if (next) {
            this.$refs[next][0].focus();
          }
          break;
        case KEY_CODE.up:
        case KEY_CODE.down:
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    triggerChange(values = this.values) {
      const { fields } = this;
      const val = values.join('');
      this.modelValue = val;
      this.$emit('change', val);
      this.$emit('update:modelValue', val);
      if (val.length >= fields) {
        this.$emit('complete', val);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.code-input-container {
  position: relative;
  direction: rtl !important;
  width: 100%;
}

.code-input {
  display: flex;
  justify-content: center;
  gap: 1rem;
  direction: rtl;

  &>input {
    width: 4rem;
    height: 4rem;
    border: 1.5px solid #d8d6de;
    border-radius: 6px;
    font-size: 20px;
    color: #525461;
    text-align: center;
    background: none;
    box-sizing: border-box;
    transition: border 0.3s ease-in-out;
    outline: none;
    -webkit-appearance: none;
  }

  &>input:focus {
    outline: none;
    border: 1.5px solid #d8d6de;
  }
}

.code-input>input:focus {
  border-color: var(--primary-dark);
}

.loading {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  text-align: center;
}

.blur {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #fff;
  opacity: 0.5;
  filter: blur(0.5px);
  transition: opacity 0.3s;
}

.title {
  margin: 0;
  height: 20px;
  padding-bottom: 10px;
}

.spin {
  display: inline-block;
  animation: loadingCircle 1s infinite linear;
}

@keyframes loadingCircle {
  100% {
    transform: rotate(360deg);
  }
}
</style>
